import { materialTypeFormat } from '@/common/material-type-formatting';
import { isValidCodeOrSerial } from '@/common/utils/scan-code-validator';
import { Scanner } from '@/components/scanner';
import { cleanCode } from '@/utils';
import { Alert, Button, Drawer, Flex, Paper, Text, TextInput, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowLeft, IconCheck, IconInfoCircle } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { MaterialType } from '../../../../../../../backend/src/types.generated';
import { useBaleOutboundContext } from '../hook';
import styles from '../styles.module.css';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Represents a bale that has been removed from the load
 */
interface RemovedBale {
	/** Display name of the material type */
	name: string;
	/** Material type enum value */
	value: MaterialType;
	/** Emoji representation of the material */
	emoji: string;
	/** Number of bales removed of this type */
	count: number;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Header section with navigation and title
 */
const PageHeader: React.FC<{ onBack: () => void }> = ({ onBack }) => (
	<Flex
		className={styles.scanHeader}
		pos="fixed"
		top={0}
		left={0}
		px="md"
		py="lg"
		bg="white"
		w="100%"
		align="center"
		gap="sm"
	>
		<IconArrowLeft onClick={onBack} style={{ cursor: 'pointer' }} />
		<Title order={2}>Remove Bags</Title>
	</Flex>
);

/**
 * Instruction alert explaining how to remove bales
 */
const InstructionAlert: React.FC = () => (
	<Flex
		pos="fixed"
		top="80px"
		left={0}
		right={0}
		px="md"
		py="sm"
		bg="transparent"
		w="100%"
		justify="center"
		style={{ zIndex: 100 }}
	>
		<Alert icon={<IconInfoCircle size={16} />} color="red" variant="light" w="100%">
			<Text size="sm">
				To remove bales from this load, scan the bag tag again or enter the code manually
			</Text>
		</Alert>
	</Flex>
);

/**
 * Displays the removal confirmation with animated material counts
 */
const RemovalConfirmation: React.FC<{
	removedBales: RemovedBale[];
	totalRemoved: number;
}> = ({ removedBales, totalRemoved }) => (
	<motion.div
		initial={{ opacity: 0, y: 20 }}
		animate={{ opacity: 1, y: 0 }}
		exit={{ opacity: 0, y: 20 }}
		transition={{ duration: 0.3, ease: 'easeOut' }}
		style={{ width: '100%' }}
	>
		<Flex px="md" py="xs" w="100%" justify="center">
			<Paper
				p="md"
				bg="white"
				w="100%"
				radius="md"
				shadow="sm"
				style={{ border: '2px solid var(--mantine-color-red-3)' }}
			>
				{/* Header */}
				<Flex align="center" gap="sm" mb="sm">
					<IconCheck size={20} color="var(--mantine-color-red-6)" />
					<Text size="sm" fw={600} c="red">
						Bales Removed from Load
					</Text>
				</Flex>

				{/* Material List */}
				<Flex direction="column" gap="xs">
					{removedBales.map((material) => (
						<motion.div
							key={material.value}
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.3 }}
						>
							<Flex align="center" justify="space-between">
								<Flex align="center" gap="xs">
									<Text size="sm">{material.emoji}</Text>
									<Text size="sm">{material.name}</Text>
								</Flex>
								<motion.div
									key={material.count}
									initial={{ scale: 1.3 }}
									animate={{ scale: 1 }}
									transition={{ duration: 0.2 }}
								>
									<Text size="sm" fw={600} c="red">
										{material.count} removed
									</Text>
								</motion.div>
							</Flex>
						</motion.div>
					))}

					{/* Total */}
					<Flex
						align="center"
						justify="space-between"
						pt="xs"
						style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}
					>
						<Text size="sm" fw={700}>
							Total
						</Text>
						<motion.div
							key={totalRemoved}
							initial={{ scale: 1.3 }}
							animate={{ scale: 1 }}
							transition={{ duration: 0.2 }}
						>
							<Text size="sm" fw={700} c="red">
								{totalRemoved} bales removed
							</Text>
						</motion.div>
					</Flex>
				</Flex>
			</Paper>
		</Flex>
	</motion.div>
);

/**
 * Bottom action buttons for manual entry and navigation
 */
const ActionButtons: React.FC<{
	onManualEntry: () => void;
	onDone: () => void;
}> = ({ onManualEntry, onDone }) => (
	<Flex
		className={styles.scanButtonContainer}
		p="md"
		bg="white"
		w="100%"
		align="center"
		justify="center"
		gap="sm"
	>
		<Button onClick={onManualEntry} fullWidth color="red">
			Enter Code to Remove
		</Button>
		<Button onClick={onDone} variant="filled" fullWidth>
			Done
		</Button>
	</Flex>
);

/**
 * Manual code entry drawer for removing bales without scanning
 */
const ManualEntryDrawer: React.FC<{
	opened: boolean;
	inputValue: string;
	isValidInput: boolean;
	onClose: () => void;
	onInputChange: (value: string) => void;
	onSubmit: () => void;
}> = ({ opened, inputValue, isValidInput, onClose, onInputChange, onSubmit }) => (
	<Drawer
		opened={opened}
		onClose={onClose}
		position="bottom"
		size="auto"
		withCloseButton={false}
		overlayProps={{ opacity: 0.5, blur: 4 }}
		transitionProps={{ transition: 'slide-up', duration: 300 }}
		zIndex={1200}
	>
		<Flex direction="column" gap="lg" p="md">
			{/* Header */}
			<Flex align="center" justify="space-between">
				<Title order={3}>Remove Code Manually</Title>
				<Button variant="subtle" onClick={onClose} size="sm">
					Cancel
				</Button>
			</Flex>

			{/* Description */}
			<Text size="sm" c="dimmed">
				Enter the code for the bag you want to remove from this load
			</Text>

			{/* Input */}
			<TextInput
				value={inputValue}
				onChange={(e) => onInputChange(e.target.value)}
				placeholder="Enter scan code to remove"
				size="lg"
				autoFocus
				inputMode="text"
				autoCapitalize="none"
				autoCorrect="off"
				spellCheck={false}
				onKeyDown={(e) => {
					if (e.key === 'Enter' && isValidInput) {
						onSubmit();
					}
				}}
				error={inputValue && !isValidInput ? 'Invalid code format' : undefined}
			/>

			{/* Actions */}
			<Flex gap="sm">
				<Button onClick={onClose} variant="outline" flex={1}>
					Cancel
				</Button>
				<Button
					onClick={onSubmit}
					disabled={!isValidInput || !inputValue.trim()}
					flex={1}
					color="red"
				>
					Remove
				</Button>
			</Flex>
		</Flex>
	</Drawer>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Page for removing bales from an outbound load by scanning or manual entry.
 *
 * This component allows users to:
 * - Scan bale codes to remove them from the current load
 * - Manually enter codes to remove bales
 * - View real-time feedback of removed bales grouped by material type
 * - Navigate back to the main outbound page
 *
 * The component tracks removal state locally and updates the main context
 * when bales are scanned for removal.
 */
export const BaleOutboundRemoveScanPage = () => {
	// ========================================================================
	// CONTEXT AND NAVIGATION
	// ========================================================================

	const { setPage, handleBaleScan, scannedBales } = useBaleOutboundContext();
	const navigate = useNavigate();

	// ========================================================================
	// LOCAL STATE
	// ========================================================================

	const [opened, { open, close }] = useDisclosure(false);
	const [inputValue, setInputValue] = useState('');
	const [removedBales, setRemovedBales] = useState<RemovedBale[]>([]);

	// ========================================================================
	// COMPUTED VALUES
	// ========================================================================

	/** Validation for manual input */
	const isValidInput = useMemo(() => isValidCodeOrSerial(inputValue), [inputValue]);

	/** Total count of removed bales across all material types */
	const totalRemoved = removedBales.reduce((sum, bale) => sum + bale.count, 0);

	/** Whether any bales have been removed */
	const hasRemovals = totalRemoved > 0;

	// ========================================================================
	// EVENT HANDLERS
	// ========================================================================

	/**
	 * Handle scanning or manual entry of a code for removal
	 *
	 * @param code - The scanned or manually entered code
	 */
	const handleRemovalScan = async (code: string) => {
		// Check if the code exists in current scanned bales before removal
		const codeExists = scannedBales.find((bale) => bale.codes.includes(code));

		if (codeExists) {
			// Track the removal immediately for UI feedback
			const config = materialTypeFormat[codeExists.value];
			setRemovedBales((prev) => {
				const existingIndex = prev.findIndex((r) => r.value === codeExists.value);
				const newRemoved = [...prev];

				if (existingIndex !== -1) {
					// Increment count for existing material type
					newRemoved[existingIndex].count += 1;
				} else {
					// Add new material type to removed list
					newRemoved.push({
						name: config.label,
						value: codeExists.value,
						emoji: config.emoji,
						count: 1,
					});
				}

				return newRemoved;
			});
		}

		// Update the main context with the removal
		await handleBaleScan('REMOVE', code);
	};

	/**
	 * Handle manual code submission
	 */
	const handleManualSubmit = () => {
		if (isValidInput && inputValue.trim()) {
			handleRemovalScan(inputValue.trim());
			setInputValue('');
			close();
		}
	};

	/**
	 * Handle closing the manual entry drawer
	 */
	const handleCloseSheet = () => {
		setInputValue('');
		close();
	};

	/**
	 * Handle back navigation to facility home
	 */
	const handleBack = () => {
		navigate({ to: '/facility/home' });
	};

	/**
	 * Handle completion of removal process
	 */
	const handleDone = () => {
		setPage('ROOT');
	};

	// ========================================================================
	// RENDER
	// ========================================================================

	return (
		<Flex className={`${styles.fullPageContainer} ${styles.pageBackgroundRed}`}>
			{/* Header */}
			<PageHeader onBack={handleBack} />

			{/* Instruction Alert */}
			<InstructionAlert />

			{/* Scanner */}
			<Scanner onScan={(code) => handleRemovalScan(cleanCode(code))} wrapperHeightPadding={160} />

			{/* Bottom Panel */}
			<Flex
				className={`${styles.scanBottomPanel} ${styles.pageBackgroundRed}`}
				pos="fixed"
				bottom={0}
				right={0}
				w="100%"
				align="center"
				direction="column"
			>
				{/* Removal Confirmation */}
				<AnimatePresence>
					{hasRemovals && (
						<RemovalConfirmation removedBales={removedBales} totalRemoved={totalRemoved} />
					)}
				</AnimatePresence>

				{/* Action Buttons */}
				<ActionButtons onManualEntry={open} onDone={handleDone} />
			</Flex>

			{/* Manual Code Entry Drawer */}
			<ManualEntryDrawer
				opened={opened}
				inputValue={inputValue}
				isValidInput={isValidInput}
				onClose={handleCloseSheet}
				onInputChange={setInputValue}
				onSubmit={handleManualSubmit}
			/>
		</Flex>
	);
};
